import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Download, FileText, Plus, Search, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api, ago, download, errMsg, KIND_LABELS } from "@/lib/api";
import { Empty, Loading, PageHeader, StatusPill } from "@/components/common";

function Claim({ c, facts, flagged }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={`cursor-help rounded px-0.5 underline decoration-dotted underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${flagged ? "bg-rose-500/10 decoration-rose-500" : "decoration-emerald-500/60"}`}>{c.text}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm text-xs">
          {c.fact_ids.length === 0 ? "No cited fact" : c.fact_ids.map((id) => <p key={id}>• {facts[id]?.text || "Unknown fact"} <span className="opacity-60">({facts[id]?.source || "?"})</span></p>)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Body({ d }) {
  const flagged = new Set((d.verification?.issues || []).map((i) => i.index));
  const c = d.content;
  if (d.kind === "ats_analysis") {
    return (
      <div className="space-y-5" data-testid="ats-analysis">
        <p className="font-display text-4xl font-bold">{c.score ?? "—"}<span className="text-base text-muted-foreground"> / 100 keyword coverage</span></p>
        {["matched_keywords", "missing_keywords", "formatting_issues", "suggestions"].map((k) => (
          <div key={k}><p className="eyebrow mb-2">{k.replace("_", " ")}</p>
            {c[k]?.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{c[k].map((x) => <li key={x}>{x}</li>)}</ul> : <p className="text-sm text-muted-foreground">None</p>}</div>
        ))}
        <p className="text-xs text-muted-foreground">Method: {d.method}{d.model ? ` (${d.model})` : ""}. ATS acceptance cannot be guaranteed.</p>
      </div>
    );
  }
  let idx = 0;
  const claim = (x) => { const i = idx++; return <Claim key={i} c={x} facts={d.facts} flagged={flagged.has(i)} />; };
  if (d.kind === "resume") {
    return (
      <div className="space-y-5 text-sm leading-relaxed" data-testid="document-body">
        {c.summary && <div><p className="eyebrow mb-1">Summary</p><p>{claim(c.summary)}</p></div>}
        {c.sections.map((s, si) => (
          <div key={si}><p className="eyebrow mb-1">{s.heading}</p><ul className="list-disc space-y-1 pl-5">{s.items.map((it) => <li key={idx}>{claim(it)}</li>)}</ul></div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-4 text-sm leading-relaxed" data-testid="document-body">
      {c.subject && <p className="font-semibold">Subject: {c.subject}</p>}
      {c.paragraphs.map((p) => <p key={idx}>{claim(p)}</p>)}
    </div>
  );
}

export function DocumentView() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const load = () => api.get(`/documents/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loading />;
  const v = d.verification;
  const approve = async () => { try { await api.post(`/documents/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const dl = (fmt) => download(`/documents/${id}/download?format=${fmt}`, `${d.kind}-${d.company}-v${d.version}.${fmt}`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-6">
      <Link to={`/jobs/${d.job_id}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-job"><ArrowLeft className="h-4 w-4" />{d.job_title} · {d.company}</Link>
      <PageHeader eyebrow={`${KIND_LABELS[d.kind]} · version ${d.version} · ${ago(d.created_at)}`} title={KIND_LABELS[d.kind]} testId="document-heading">
        <StatusPill status={d.status} testId="document-status" />
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <section className="panel p-6 sm:p-8"><Body d={d} /></section>
        <aside className="space-y-4">
          <div className={`panel p-5 ${v.passed ? "" : "border-rose-500/40"}`} data-testid="verification-panel">
            <p className="eyebrow mb-3 flex items-center gap-1">{v.passed ? <CheckCircle2 className="h-3.5 w-3.5 text-brand" /> : <ShieldAlert className="h-3.5 w-3.5 text-destructive" />}Truthfulness check</p>
            <p className="text-sm">{v.passed ? `All ${v.checked ?? ""} claims trace to confirmed facts.` : `${v.issues.length} unsupported claim(s) — this version is blocked.`}</p>
            {v.ai_check && <p className="mt-1 text-xs text-muted-foreground">Independent AI check: {v.ai_check}</p>}
            {v.note && <p className="mt-1 text-xs text-muted-foreground">{v.note}</p>}
            {v.issues?.length > 0 && <ul className="mt-3 space-y-2 text-xs" data-testid="verification-issues">{v.issues.map((i, k) => <li key={k} className="rounded-md bg-rose-500/5 p-2"><span className="font-medium">“{i.claim}”</span><br />{i.reason}</li>)}</ul>}
          </div>
          <div className="panel space-y-2 p-5">
            <p className="eyebrow mb-1">Your decision</p>
            {d.status === "approved" ? <p className="text-sm text-brand" data-testid="document-approved-note">Approved by you. Apply on the employer's site, then mark it applied in the tracker.</p>
              : <Button className="w-full" disabled={!v.passed} onClick={approve} data-testid="approve-document-button">Approve this version</Button>}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" disabled={d.status === "blocked"} onClick={() => dl("pdf")} data-testid="download-pdf-button"><Download className="mr-1 h-4 w-4" />PDF</Button>
              <Button variant="outline" disabled={d.status === "blocked"} onClick={() => dl("docx")} data-testid="download-docx-button"><Download className="mr-1 h-4 w-4" />DOCX</Button>
            </div>
            <p className="text-xs text-muted-foreground">Versions are permanent. Regenerate from the job page to create a new version. Recruiter emails are drafts — AJOBF never sends them.</p>
          </div>
          {d.model && <p className="text-xs text-muted-foreground">Generated by {d.model}</p>}
        </aside>
      </div>
    </div>
  );
}

export default function Studio() {
  const [docs, setDocs] = useState(null);
  const [kind, setKind] = useState("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { api.get("/documents").then((r) => setDocs(r.data)).catch((e) => setError(errMsg(e))); }, []);
  const visible = docs?.filter((d) => (kind === "all" || d.kind === kind) && `${d.company} ${d.job_title}`.toLowerCase().includes(query.toLowerCase())) || [];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Your experience, thoughtfully presented" title="Document studio" testId="studio-heading"><Button asChild><Link to="/jobs"><Plus size={16} />Create from a job</Link></Button></PageHeader>
      <div className="workspace-banner"><h2 className="text-xl font-bold">A stronger application. Still entirely you.</h2><p className="mt-2 max-w-xl text-sm leading-relaxed text-blue-100/80">Tailor your resume, write a cover letter or prepare a recruiter draft. Every claim stays linked to facts you have confirmed.</p><div className="mt-5 flex flex-wrap gap-4 text-xs text-blue-100"><span>Verified career facts</span><span>PDF & DOCX exports</span><span>Permanent version history</span></div></div>
      <div className="flex flex-wrap gap-3"><div className="relative min-w-48 flex-1"><Search size={16} className="absolute left-3 top-3 text-muted-foreground" /><Input className="h-10 pl-9" aria-label="Search documents" placeholder="Search company or role" value={query} onChange={(e) => setQuery(e.target.value)} /></div><select aria-label="Document type" value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-lg border bg-card px-3 text-sm"><option value="all">All documents</option>{Object.entries(KIND_LABELS).map(([k, label]) => <option value={k} key={k}>{label}</option>)}</select></div>
      {error && <p className="panel p-5 text-sm text-destructive" role="alert">{error}</p>}
      {!docs ? (!error && <Loading />) : visible.length === 0 ? <Empty icon={FileText} title="No documents yet" testId="studio-empty">Open a job and generate a tailored resume, cover letter or recruiter draft.</Empty> : (
        <div className="panel divide-y" data-testid="studio-list">
          {visible.map((d) => (
            <Link key={d.id} to={`/studio/${d.id}`} data-testid={`studio-doc-${d.id}`} className="flex flex-col gap-1 p-4 hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between">
              <div><p className="font-medium">{KIND_LABELS[d.kind]} · v{d.version}</p><p className="text-xs text-muted-foreground">{d.job_title} · {d.company} · {ago(d.created_at)}</p></div>
              <StatusPill status={d.status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
